import React from 'react';
import type { FC } from 'react';

import type { LoadableType } from '@confluence/loadable';
import {
	LoadableAfterPaint,
	LoadableActivatable,
	ActivationTriggerType,
} from '@confluence/loadable';
import { expVal } from '@confluence/feature-experiments';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { SSR_PARTIAL_COMPONENT } from '@confluence/ssr-utilities/entry-points/ssrPartialSuccess.ts';

import { EndOfPageRecPlaceholderContainer } from './SharedComponents.tsx';
import type { EndOfPageRecommendationProps } from './EndOfPageRecommendation.tsx';

export const EndOfPageRecommendationLoader: LoadableType<EndOfPageRecommendationProps> =
	LoadableAfterPaint({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-EndOfPageRecommendation" */ './EndOfPageRecommendation.tsx'
				)
			).EndOfPageRecommendation,
	});

export const EndOfPageRecommendationLoaderWithScrollIntoView: LoadableType<EndOfPageRecommendationProps> =
	LoadableActivatable({
		loader: async () =>
			(
				await import(
					/* webpackChunkName: "loadable-EndOfPageRecommendation" */ './EndOfPageRecommendation.tsx'
				)
			).EndOfPageRecommendation,
		triggers: [
			{
				type: ActivationTriggerType.SCROLL_INTO_VIEW,
			},
		],
		ssrSuccessCheckName: SSR_PARTIAL_COMPONENT.EndOfPageRecommendation,
	});

export const EndOfPageRecommendationFinalLoader: FC<EndOfPageRecommendationProps> = (props) => {
	if (expVal('confluence_end_of_page_recs_suspense', 'isEnabled', false)) {
		// Use EndOfPageRecommendationLoader (LoadableAfterPaint) instead of
		// EndOfPageRecommendationLoaderWithScrollIntoView (LoadableActivatable) in the streaming path.
		// LoadableActivatable's SCROLL_INTO_VIEW trigger fires setIsInViewport() synchronously via a
		// ref callback during React's commit/hydration phase, causing React error #421 when nested
		// inside a SuspenseWithDataFetcher boundary while scheduleSyncHydration is active.
		return (
			<SuspenseWithDataFetcher
				dataFetcherName="end-of-page-recommendations"
				fallback={<EndOfPageRecPlaceholderContainer />}
			>
				<EndOfPageRecommendationLoader {...props} />
			</SuspenseWithDataFetcher>
		);
	}
	return <EndOfPageRecommendationLoaderWithScrollIntoView {...props} />;
};
